import React, { useState, useMemo, useEffect } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import { debounce } from '@mui/material/utils'
import { connectionsPanelSx } from './ConnectionsLayout'
import type { WidgetConnectionsV2Props } from './types'

export function WidgetConnectionsV2({
  widgetModule,
  data,
}: WidgetConnectionsV2Props) {
  const { Connections, useHotglue } = widgetModule
  const { setOptions } = useHotglue()
  const [search, setSearch] = useState('')

  useEffect(() => {
    const options = {
      ...data.options,
      ...(data.jwt ? { jwtToken: data.jwt } : {}),
    }
    setOptions(options)
  }, [data.jwt, data.options, setOptions])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearch(e?.target?.value ?? '')
  }

  const debouncedHandleChange = useMemo(
    () => debounce(handleChange, 300),
    []
  )

  useEffect(() => {
    return () => debouncedHandleChange.clear()
  }, [debouncedHandleChange])

  const filterFlow = (flow: string) =>
    data.flow ? data.flow === flow : true

  // Third param (isTarget) is required by Connections filterEntity signature but unused here
  const filterEntity = (
    _flowId: string,
    entityId: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- required by widget Connections API
    _isTarget?: boolean
  ): boolean => {
    if (search)
      return entityId.toLowerCase().includes(search.trim().toLowerCase())
    if (data.flow && data.entityId) return entityId === data.entityId
    return true
  }

  return (
    <Paper sx={connectionsPanelSx}>
      {data.type === 'all' && (
        <Box display="flex" flexDirection="column" gap={2} mb={3}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search app"
            type="text"
            onChange={debouncedHandleChange}
          />
        </Box>
      )}
      <Connections
        tenant={data.tenant}
        filterFlow={filterFlow}
        filterEntity={filterEntity}
        operation={data.options?.operation}
        nextStep={data.options?.nextStep}
        hideBackButtons
      />
    </Paper>
  )
}
